import classNames from "classnames";
import { PreviewRenderer, slateToPlainText } from "./PreviewRenderer";
import { timeDifference } from "../../lib/utils/time";
import { useEffect, useState, useRef } from "react";
import { Icon } from "../common/Icon";
import { Message as MessageType } from "../../lib/types";
import { useUserContext } from "../../providers/UserContext";
import { MessageComposer } from "./MessageComposer";
import { Descendant } from "slate";
import { editMessage, react, removeReaction } from "../../lib/database";
import Picker from "@emoji-mart/react";
import data, { EmojiMartData } from "@emoji-mart/data";
import { User } from "firebase/auth";
import { truncate } from "../../lib/utils/formatting";
import { Button } from "../common/Button";
import { Link } from "react-router-dom";
import rangy from "rangy";
import "rangy/lib/rangy-classapplier";
import "rangy/lib/rangy-serializer";

export const SystemMessage = ({
                          author,
                          content,
                          timestamp,
                          isRead = false,
                          isOriginal = false,
                          message,
                          previousMessage,
                          lastMessage,
                          scrollChatToBottom,
                          onReply,
                          scrollToMessage,
                          highlight,
                          onThread,
                          threadText,
                          showAuthor=true,
                          showTimestamp=true,
                        }: {
  author: string;
  content: string;
  isRead?: boolean;
  isOriginal?: boolean;
  timestamp: number;
  message: MessageType;
  previousMessage?: MessageType;
  lastMessage: boolean;
  scrollChatToBottom: () => void;
  onReply?: (message: MessageType) => void;
  onThread?: (message: MessageType, markedHtml: string, selectedHtml: string) => void;
  scrollToMessage?: (message: MessageType) => void;
  highlight?: boolean;
  threadText?: string;
  showAuthor?: boolean;
  showTimestamp?: boolean;
}) => {
  const emojis = data as EmojiMartData;
  let className = "flex group ";
  let textClass = "font-style-body-md";
  let authorClass = "font-style-label-sm flex items-center gap-1";
  if (isRead) {
    if (!isOriginal) {
      textClass += " !text-gray-4 read-message";
      authorClass += " !text-gray-4 ";
    } else {
      textClass += " !text-gray-4 pl-2 border-l-[3px] border-l-gray-6";
    }
  } else {
    textClass += " !text-gray-1";
  }
  const json = JSON.parse(content);
  const [now, setNow] = useState(Date.now());
  useEffect(() => {
    const interval = setInterval(() => {
      setNow(Date.now());
    }, 60000);
    return () => clearInterval(interval);
  }, []);
  const { user, getMember } = useUserContext();
  const isMine = message?.authorId === user?.uid;
  const [isEditting, setIsEditting] = useState(false);
  const [showEmojiPicker, setShowEmojiPicker] = useState(false);
  const [pickerPosition, setPickerPosition] = useState({ top: 0, left: 0 });
  const [hoverContent, setHoverContent] = useState("");
  const handleEmojiSelect = (emoji: any) => {
    if (message && user) {
      react(message, user, emoji.id);
    }
    setShowEmojiPicker(false); // Hide the picker after selection
  };

  if (showAuthor) {
    className += "mt-4";
  }
  // Edit on keydown (up) if last message
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (lastMessage && isMine && event.key === "ArrowUp" && !isEditting) {
        event.preventDefault();
        setIsEditting(true);
        setTimeout(() => {
          scrollChatToBottom();
        }, 0);
      }
      // Cancel editing on Esc
      if (isEditting && event.key === "Escape") {
        setIsEditting(false);
      }
    };
    const handleKeyUp = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        setIsEditting(false);
        setShowEmojiPicker(false);
      }
    };
    window.addEventListener("keydown", handleKeyDown);
    window.addEventListener("keyup", handleKeyUp);
    return () => {
      window.removeEventListener("keyup", handleKeyUp);
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, [lastMessage]);

  const reactions = message.reactions || {};
  const didIReact = Object.keys(reactions).some((reaction) =>
    Object.keys(reactions[reaction]).includes(user?.uid || "")
  );
  if (highlight) {
    className += " !bg-blue-4";
  }
  const [activeRangeMessage, setActiveRangeMessage] =
    useState<MessageType | null>(null);
  const [selection, setSelection] = useState<any>(null);
  const isSelectionWithinSingleMessage = (id: string) => {
    const selection = window.getSelection();
    if (selection && !selection.isCollapsed) {
      const startContainer = selection.anchorNode?.parentElement;
      const endContainer = selection.focusNode?.parentElement;

      // Ensure start and end containers belong to the same message
      const startMessage = startContainer?.closest("[data-message-id]");
      const endMessage = endContainer?.closest("[data-message-id]");
      return (
        startMessage &&
        endMessage &&
        startMessage === endMessage &&
        startMessage.getAttribute("data-message-id") === id
      );
    }
    return false;
  };
  const [selectionBounds, setSelectionBounds] = useState<{
    top: number;
    left: number;
  } | null>(null);
  const [showCreateThread, setShowCreateThread] = useState(false);

  const messageContentRef = useRef<HTMLDivElement>(null);

  // Helper function to find the corresponding node in the clone
  const findNodeInClone = (
    originalNode: Node,
    originalRoot: Node,
    clonedRoot: Node
  ): Node => {
    const path = [];
    let node: Node | null = originalNode;
    while (node && node !== originalRoot) {
      const parent = node.parentNode as HTMLElement;
      if (!parent) break;
      const index = Array.prototype.indexOf.call(parent.childNodes, node);
      path.unshift(index);
      node = parent;
    }
    let clonedNode: Node = clonedRoot;
    for (const index of path) {
      clonedNode = clonedNode.childNodes[index];
    }
    return clonedNode;
  };

  useEffect(() => {
    const handleSelectionChange = () => {
      setShowCreateThread(false);
    }
    document.addEventListener("selectionchange", () => {
      handleSelectionChange();
    });
    return () => {
      document.removeEventListener("selectionchange", () => {
        handleSelectionChange();
      });
    };
  }, []);

  const createThreadFromSelection = () => {
    const selectionObj = rangy.getSelection();
    const selectedText = selectionObj.toString();
    if (selectionObj && selectionObj.rangeCount > 0) {
      const messageContentElement = messageContentRef.current;
      if (!messageContentElement) return;

      const range = selectionObj.getRangeAt(0);

      // Clone the message content element
      const clonedMessageContent = messageContentElement.cloneNode(true) as HTMLElement;

      // Adjust the range to point to the nodes within the cloned content
      const clonedRange = rangy.createRange();
      clonedRange.setStart(
        findNodeInClone(range.startContainer, messageContentElement, clonedMessageContent),
        range.startOffset
      );
      clonedRange.setEnd(
        findNodeInClone(range.endContainer, messageContentElement, clonedMessageContent),
        range.endOffset
      );

      // Create a class applier for highlighting
      const classApplier = rangy.createClassApplier("text-highlight", {
        elementTagName: "span",
        elementAttributes: {},
        normalize: true,
      });

      // Apply the class applier to the cloned range
      classApplier.applyToRange(clonedRange);

      // Get the innerHTML of the cloned message content with the highlighted selection
      const selectedHtml = clonedMessageContent.innerHTML;

      // Pass the HTML to the onThread function
      onThread && onThread(message, selectedHtml, selectedText);

      setShowCreateThread(false);

      // Clear the selection
      selectionObj.removeAllRanges();
    }
  };

  return (
    <div data-message-id={message.id}>
      {selectionBounds && showCreateThread && (
        <div
          className="fixed z-50 bg-gray-900 text-white p-2 rounded"
          style={{
            top: selectionBounds.top - 55,
            left: selectionBounds.left - 80,
          }}
        >
          <Button onClick={createThreadFromSelection}>Start Thread</Button>
        </div>
      )}
      <div className={className}>
        {showEmojiPicker && (
          <div>
            <div
              className="backdrop fixed z-40 left-0 top-0 w-full h-full"
              onClick={() => {
                setShowEmojiPicker(false);
              }}
            ></div>
            <div
              className="fixed z-50"
              style={{
                top: Math.min(pickerPosition.top - 50, window.innerHeight - 500),
                left: pickerPosition.left - 380,
              }}
            >
              <Picker data={data} onEmojiSelect={handleEmojiSelect} />
            </div>
          </div>
        )}
        <div className="flex flex-col gap-1 w-full relative group block hover:bg-gray-9 pb-[2px]">
          {message.parent && (
            <button
              className={"relative rounded-t-lg py-1"}
              onClick={() => {
                if (scrollToMessage) {
                  scrollToMessage(message.parent as MessageType);
                }
              }}
            >
              <div className="flex items-center gap-2 w-full">
                <div className="w-3">
                  <span
                    className="absolute -top-[1px]  left-0 mt-0 origin-center rotate-180"
                    style={{
                      transform: "scaleX(-1)",
                    }}
                  >
                    <Icon name={"reply"} size={"xsmall"} />
                  </span>
                </div>

                <div className="flex gap-1">
                  <div className="font-style-body-xs text-gray-1">
                    {message.parent.authorName}:
                  </div>
                  <div className="font-style-body-xs text-gray-3">
                    {truncate(message.parent.plainText, 60)}
                  </div>
                </div>
              </div>
            </button>
          )}
          <div className="flex justify-between items-center w-full group">
            <div className={authorClass}>
              <Icon name={"info"} size={"xsmall"} />
              <span>
                {author}:
              </span>
              <span className="body font-style-body-sm">{message.plainText}</span>
            </div>
          </div>
          <div
            className="actions z-10  flex items-center gap-2 absolute pointer-events-none group-hover:pointer-events-auto opacity-0 group-hover:opacity-100 hover:!opacity-100 transition-all right-0 top-[2px]"
          >
            {(
              <span className="px-2  font-style-body-xs text-gray-3 ml-1"
                    title={new Date(timestamp).toLocaleString()}
              >
                {timeDifference(now, timestamp)} {" "}
              </span>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};
